import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Store, UtensilsCrossed, Tags, Sparkles, TrendingUp,
  Palette, Clock, Link2, User, LogOut, Menu, Eye, RefreshCw, ExternalLink, Copy,
} from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Toggle } from "@/components/dashboard/ui";
import { BrandLockup } from "@/components/Brand";
import StoreModule from "@/components/dashboard/StoreModule";
import ProductsModule from "@/components/dashboard/ProductsModule";
import CategoriesModule from "@/components/dashboard/CategoriesModule";
import ContentModule from "@/components/dashboard/ContentModule";
import ConversionModule from "@/components/dashboard/ConversionModule";
import { ServiceModule, LinkModule, AccountModule } from "@/components/dashboard/Settings";

const NAV = [
  ["overview", "Visão Geral", LayoutDashboard],
  ["store", "Minha Loja", Store],
  ["products", "Produtos", UtensilsCrossed],
  ["categories", "Categorias", Tags],
  ["content", "Conteúdo", Sparkles],
  ["conversion", "Conversão", TrendingUp],
  ["service", "Atendimento", Clock],
  ["link", "Meu Link", Link2],
  ["account", "Minha Conta", User],
];

export default function Dashboard() {
  const { rid: paramRid } = useParams();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const rid = paramRid || user?.restaurant_id;
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [sidebar, setSidebar] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);

  useEffect(() => {
    if (!rid) { nav(user?.role === "superadmin" ? "/admin" : "/login"); return; }
    load();
    // eslint-disable-next-line
  }, [rid]);

  const load = async () => {
    try {
      const { data } = await api.get(`/restaurants/${rid}/full`);
      setData(data);
      setPreviewKey((k) => k + 1);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const patchStore = async (patch) => {
    setData((d) => ({ ...d, restaurant: { ...d.restaurant, ...patch } }));
    try {
      await api.put(`/restaurants/${rid}`, patch);
      setPreviewKey((k) => k + 1);
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!data) return <div className="min-h-screen bg-[#0C0C0E] flex items-center justify-center text-zinc-500">Carregando painel...</div>;
  const r = data.restaurant;
  const props = { rid, data, reload: load, patchStore, setTab };

  return (
    <div className="min-h-screen bg-[#0C0C0E] text-zinc-100 flex">
      {/* SIDEBAR */}
      <aside className={`fixed lg:static z-50 inset-y-0 left-0 w-64 bg-[#121215] border-r border-white/5 p-4 flex flex-col transition-transform ${sidebar ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="px-2 py-3">
          <BrandLockup />
          <p className="text-xs text-zinc-500 mt-2 truncate">{r.name}</p>
        </div>
        <nav className="flex-1 mt-3 space-y-1 overflow-y-auto no-scrollbar">
          {NAV.map(([key, label, Icon]) => (
            <button key={key} onClick={() => { setTab(key); setSidebar(false); }} data-testid={`nav-${key}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${tab === key ? "bg-[#FF6B00] text-white" : "text-zinc-400 hover:bg-white/5 hover:text-white"}`}>
              <Icon size={18} /> {label}
            </button>
          ))}
        </nav>
        <button onClick={logout} data-testid="dash-logout" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-zinc-400 hover:bg-white/5 mt-2"><LogOut size={18} /> Sair</button>
      </aside>
      {sidebar && <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setSidebar(false)} />}

      {/* MAIN */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 border-b border-white/5 bg-[#0C0C0E]/80 backdrop-blur flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebar(true)} className="lg:hidden"><Menu size={22} /></button>
            <h1 className="font-head text-lg font-extrabold">{NAV.find((n) => n[0] === tab)?.[1]}</h1>
          </div>
          <a href={`/${r.slug}`} target="_blank" rel="noreferrer" data-testid="preview-mobile-btn" className="xl:hidden flex items-center gap-1.5 text-sm bg-white/10 px-3 py-2 rounded-full"><Eye size={15} /> Ver prévia</a>
        </header>

        <div className="flex-1 flex">
          <main className="flex-1 min-w-0 p-4 lg:p-8 max-w-4xl">
            {tab === "overview" && <Overview {...props} />}
            {tab === "store" && <StoreModule {...props} />}
            {tab === "products" && <ProductsModule {...props} />}
            {tab === "categories" && <CategoriesModule {...props} />}
            {tab === "content" && <ContentModule {...props} />}
            {tab === "conversion" && <ConversionModule {...props} />}
            {tab === "service" && <ServiceModule {...props} />}
            {tab === "link" && <LinkModule {...props} />}
            {tab === "account" && <AccountModule {...props} />}
          </main>

          {/* PREVIEW */}
          <aside className="hidden xl:block w-[400px] shrink-0 border-l border-white/5 bg-[#0A0A0C] p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-zinc-400 uppercase tracking-wide">Prévia do cardápio</span>
              <button onClick={() => setPreviewKey((k) => k + 1)} className="text-zinc-500 hover:text-white"><RefreshCw size={15} /></button>
            </div>
            <div className="mx-auto w-[300px] h-[620px] rounded-[2rem] border-4 border-zinc-800 overflow-hidden bg-black sticky top-6">
              <iframe key={previewKey} src={`/${r.slug}`} title="preview" loading="lazy" className="w-full h-full" />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

const STATUS_LABEL = { em_implantacao: "Em implantação", ativo: "Ativo", pagamento_pendente: "Pagamento pendente", suspenso: "Suspenso", cancelado: "Cancelado" };

function Overview({ data, patchStore, setTab }) {
  const r = data.restaurant;
  const link = `${window.location.origin}/${r.slug}`;
  const stat = [
    ["Produtos", data.products.length],
    ["Categorias", data.categories.length],
    ["Stories", data.stories.length],
    ["Vídeos", data.videos.length],
  ];
  return (
    <div className="space-y-5" data-testid="overview">
      <div className="grid sm:grid-cols-2 gap-4">
        <Card>
          <p className="text-xs text-zinc-500">Status da conta</p>
          <p className="font-head text-xl font-extrabold mt-1 text-[#FF6B00]">{STATUS_LABEL[r.status]}</p>
          <p className="text-xs text-zinc-500 mt-1">Vencimento: {r.due_date || "—"}</p>
        </Card>
        <Card>
          <p className="text-xs text-zinc-500 mb-2">Aceitando pedidos</p>
          <Toggle checked={r.accepting_orders !== false} onChange={(v) => patchStore({ accepting_orders: v })} tid="overview-accepting" label={r.accepting_orders !== false ? "Aberto para pedidos" : "Pedidos pausados"} />
        </Card>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {stat.map(([l, v]) => <Card key={l}><p className="font-head text-3xl font-extrabold">{v}</p><p className="text-xs text-zinc-500">{l}</p></Card>)}
      </div>
      <Card title="Ações rápidas">
        <div className="flex flex-wrap gap-2">
          {[["products", "Adicionar produto"], ["content", "Criar story"], ["store", "Editar loja"], ["link", "Meu link"]].map(([t, l]) =>
            <button key={t} onClick={() => setTab(t)} className="rounded-full bg-white/5 hover:bg-white/10 px-4 py-2 text-sm font-semibold">{l}</button>)}
        </div>
      </Card>
      <Card title="Link público">
        <div className="flex items-center gap-2">
          <input readOnly value={link} className="flex-1 rounded-xl bg-white/[0.04] border border-white/10 px-4 py-2.5 text-sm text-zinc-400" data-testid="overview-link" />
          <button onClick={() => { navigator.clipboard.writeText(link); toast.success("Copiado"); }} className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center"><Copy size={16} /></button>
          <a href={link} target="_blank" rel="noreferrer" className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center"><ExternalLink size={16} /></a>
        </div>
      </Card>
    </div>
  );
}
